import React, { useMemo } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

// import { setAgentAndSubAgent } from "../redux/slices/tableSlice";
import { selectAgentMapByBU } from "../../../../../features/dashboard/selectors";
import { useSelector } from "react-redux";

const ITEM_HEIGHT = 42;
const ITEM_PADDING_TOP = 6;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 260,
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#f5f5f5",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#c1c1c1",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#a8a8a8",
      },
      scrollbarWidth: "thin",
      scrollbarColor: "#c1c1c1 #f5f5f5",
    },
  },
};

/* ---------------- helpers ---------------- */

const parseValue = (value) => {
  if (!value?.includes("::")) {
    return { agent: value, sub: "" };
  }
  const [agent, sub] = value.split("::");
  return { agent, sub };
};

const getDisplayLabel = (agent, sub) => (sub ? `${agent} - ${sub}` : agent);

/* ---------------- component ---------------- */

const IndicationsDropdown = ({
  businessUnit,
  selectedValues = [],
  onSelectionChange,
  disabled = false,
  label = "Select Indications",
}) => {
  const agentMapByBU = useSelector(selectAgentMapByBU);

  /** Build dropdown options only when BU changes */
  const options = useMemo(() => {
    // const agentMap = AGENT_MAPS_BY_BU[businessUnit] || {};
    const agentMap = agentMapByBU?.[businessUnit] ?? {};

    return Object.entries(agentMap).flatMap(
      ([agentKey, { display, subagents }]) => {
        if (!subagents?.length) {
          return [{ value: agentKey, label: display }];
        }

        return [
          { value: agentKey, label: display },
          ...subagents.map((sub) => ({
            value: `${agentKey}::${sub}`,
            label: `${display} - ${sub}`,
          })),
        ];
      },
    );
  }, [agentMapByBU, businessUnit]);

  const handleChange = (event) => {
    const updated =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    onSelectionChange(updated);

    /** Dispatch only the last selected item */
    // const lastSelected = updated.at(-1);
    // if (lastSelected) {
    //   const { agent, sub } = parseValue(lastSelected);
    //   dispatch(
    //     setAgentAndSubAgent({
    //       agentName: agent,
    //       subAgentName: sub,
    //     }),
    //   );
    // }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  return (
    <FormControl
      sx={{
        width: 290,
        position: "relative",
        borderRadius: "10px",
      }}
    >
      <InputLabel
        id="indications-select-label"
        sx={{
          fontSize: "0.85rem",
          borderRadius: "10px",
          transform: "translate(14px, 10px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        }}
      >
        {label}
      </InputLabel>

      <Select
        multiple
        disabled={disabled}
        labelId="indications-select-label"
        value={selectedValues}
        onChange={handleChange}
        input={
          <OutlinedInput
            label={label}
            sx={{
              height: 40,
              display: "flex",
              alignItems: "center",
              pr: selectedValues.length ? 6 : 2,
            }}
          />
        }
        MenuProps={MenuProps}
        renderValue={(selected) => {
          if (!selected.length) return null;

          const first = selected[0];
          const remaining = selected.length - 1;

          const { agent, sub } = parseValue(first);

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title={getDisplayLabel(agent, sub)} arrow>
                <Chip
                  size="small"
                  label={getDisplayLabel(agent, sub)}
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    "& .MuiChip-label": { px: 0.6 },
                  }}
                />
              </Tooltip>

              {remaining > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  +{remaining} more
                </Typography>
              )}
            </Box>
          );
        }}
        sx={{
          "& .MuiSelect-select": {
            paddingY: 0.5,
            fontSize: "14px",
            cursor: disabled ? "not-allowed" : "pointer",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "#ccc" : undefined,
          },
          "&.MuiSelect-root": {
            borderRadius: "10px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "#ccc !important" : "#1976d2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "#ccc !important" : "#1976d2",
          },

          pointerEvents: disabled ? "not-allowed" : "auto",
          opacity: disabled ? 0.4 : 1,
          height: 36,
          width: 230,
        }}
      >
        {!options.length && (
          <MenuItem disabled>No indications available</MenuItem>
        )}

        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{
              fontSize: "13px",
              marginBottom: "3px",
              borderRadius: "3px",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>

      {!disabled && selectedValues.length > 0 && (
        <IconButton
          size="small"
          onClick={handleClear}
          sx={{
            position: "absolute",
            right: 28,
            top: "50%",
            transform: "translateY(-50%)",
            color: "text.secondary",
            zIndex: 2,
            p: 0.4,
            "&:hover": { color: "text.primary" },
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </FormControl>
  );
};

export default IndicationsDropdown;
